import { UserDirectoryApi } from '../api/userdirectory/user-directory-api';
import type {
  SSEUserDirectoryUpdated,
  UserDirectoryUserDto,
} from '../api/agwa-api-model';
import { getSseHub } from '../utility/notifications/sse-hub';
import { ApiFactory } from '../api/agwasuite-api-factory';
import { isUnanswered } from '../api/unanswered-request';

/** Callback invoked when a user is updated */
export type UserUpdatedCallback = (user: UserDirectoryUserDto) => void;

/**
 * Simplified user directory cache.
 * Single in-memory map; no TTL. A lookup already in flight for an id is shared rather than
 * repeated, an answered absence is remembered (isKnownAbsent) and so is a lookup that got no
 * answer (wasUnanswered).
 * Provided functions:
 *  - preFetch(ids) : fetch distinct ids not already cached; merge.
 *  - getUserDto(id) : async retrieval (fetch if missing then return).
 *  - getUserDtoSync(id) : synchronous lookup (undefined if missing).
 *  - subscribe(userId, callback) : be notified when a specific user is updated.
 *  - subscribeEdits(callback) : be notified of every person the user-updated event delivers.
 * Compatibility shims retained: resolve(ids) + get(id) used by existing code.
 */
export interface UserDirectoryCache {
  preFetch(ids: (string | null | undefined)[]): Promise<void>;
  getUserDto(id: string): Promise<UserDirectoryUserDto | undefined>;
  getUserDtoSync(id: string): UserDirectoryUserDto | undefined;
  /**
   * True only when the server ANSWERED a lookup for this id and returned nobody. A lookup that got
   * no answer leaves this false, so a name whose request failed is not mislabelled as an unknown
   * person (D-144: an unanswered request presents nothing).
   */
  isKnownAbsent(id: string): boolean;
  /**
   * True when the most recent lookup for this id got no answer at all (D-144): the network
   * refused it or it timed out. A first load that sees this waits for the server to answer and
   * asks again, rather than leaving the name blank for the life of the page.
   */
  wasUnanswered(id: string): boolean;
  // Legacy consumers:
  resolve(
    ids: string[]
  ): Promise<Map<string, UserDirectoryUserDto | undefined>>;
  get(id: string): UserDirectoryUserDto | undefined;
  prime(users: UserDirectoryUserDto[]): void;
  listen(): void;
  /** Subscribe to updates for a specific user. Returns unsubscribe function. */
  subscribe(userId: string, callback: UserUpdatedCallback): () => void;
  /**
   * Be told of every person the server's user-updated event delivers, whoever they are, and only
   * of those: a person primed from a lookup's answer is not an edit, so it is not passed on. For a
   * reader that keys on something other than one person, such as the company's people list, which
   * must hear a person it does not hold yet (#398 C5). Returns the unsubscribe function.
   */
  subscribeEdits(callback: UserUpdatedCallback): () => void;
  /**
   * What the user-updated event does on arrival: prime the person, then tell subscribeEdits'
   * callbacks. listen() routes the event here; it is public so a spec can deliver the event
   * exactly as the SSE listener does.
   */
  applyUserUpdated(user: UserDirectoryUserDto): void;
  /**
   * Show these bytes as a user's avatar right now, without waiting for a refetch.
   *
   * The stored avatar URL is DETERMINISTIC by design - see
   * `documentation/engineering/system-image-caching-standard.md`. It does not change when the image
   * behind it does, so after an upload every render site sets an `src` identical to the one already
   * there, the browser issues no request, and ETag revalidation never gets its chance. The user
   * keeps seeing the old picture until a hard reload.
   *
   * The surface that CHANGED the image is the one place that already holds the new bytes, so it
   * hands them over here rather than asking the network for something it just sent. Every reader
   * goes through this cache, so one call updates the profile page, the header and every
   * user-name card at once.
   *
   * Deliberately NOT a cache-buster on the URL: that would defeat caching for every viewer of every
   * avatar in order to refresh one, which is the combination the caching standard exists to prevent.
   */
  setLocalProfileImage(userId: string, imageUrl: string): void;
  /** Fetch all users in a member group and prime the cache. Returns the user list. */
  getAllUsersForMember(loginGroupId: string): Promise<UserDirectoryUserDto[]>;
}

class SimpleUserDirectoryCacheImpl implements UserDirectoryCache {
  private readonly map = new Map<string, UserDirectoryUserDto>();
  /** Ids the server answered for with nobody; see isKnownAbsent. */
  private readonly absent = new Set<string>();
  /** Ids whose last lookup got no answer; see wasUnanswered. */
  private readonly unanswered = new Set<string>();
  /**
   * The lookup already in flight for each id. Every <user-name> for a person asks when it
   * connects, so a table naming the same person on twenty rows would otherwise send twenty
   * requests; a caller asking for an id already on its way waits for that request instead.
   */
  private readonly inFlight = new Map<string, Promise<void>>();
  /** Subscribers per user ID */
  private readonly subscribers = new Map<string, Set<UserUpdatedCallback>>();
  /** Subscribers to every edit the user-updated event delivers; see subscribeEdits. */
  private readonly editSubscribers = new Set<UserUpdatedCallback>();
  sseAttached = false;
  constructor() {}

  api() {
    return ApiFactory.authenticated(UserDirectoryApi);
  }

  async preFetch(ids: (string | null | undefined)[]): Promise<void> {
    const distinct = Array.from(
      new Set(
        ids.filter((v): v is string => typeof v === 'string' && v.length > 0)
      )
    ).filter((id) => !this.map.has(id));
    if (!distinct.length) return;
    const waiting = distinct
      .map((id) => this.inFlight.get(id))
      .filter((p): p is Promise<void> => p !== undefined);
    const toAsk = distinct.filter((id) => !this.inFlight.has(id));
    if (toAsk.length) {
      const request = this.fetchIds(toAsk);
      for (const id of toAsk) this.inFlight.set(id, request);
      // Cleared whether it answered or threw, so a later lookup can ask again.
      void request.then(
        () => this.clearInFlight(toAsk, request),
        () => this.clearInFlight(toAsk, request)
      );
      waiting.push(request);
    }
    await Promise.all(waiting);
  }

  private clearInFlight(ids: string[], request: Promise<void>) {
    for (const id of ids)
      if (this.inFlight.get(id) === request) this.inFlight.delete(id);
  }

  /** One directory request for ids nobody is already asking about. */
  private async fetchIds(ids: string[]): Promise<void> {
    const resp = await this.api().getUserDirectory(ids);
    if (!resp.success) {
      // A failure that got no answer is remembered so a first load can wait and ask again; any
      // other failure is silent and callers get undefined.
      if (isUnanswered(resp)) for (const id of ids) this.unanswered.add(id);
      return;
    }
    for (const id of ids) this.unanswered.delete(id);
    const users = resp.data.users || [];
    for (const u of users) if (u.loginUserId) this.map.set(u.loginUserId, u);
    // The server answered: whoever it did not return is known not to exist (or not to be visible).
    for (const id of ids) if (!this.map.has(id)) this.absent.add(id);
  }

  isKnownAbsent(id: string): boolean {
    return this.absent.has(id);
  }

  wasUnanswered(id: string): boolean {
    return this.unanswered.has(id);
  }

  async getUserDto(id: string): Promise<UserDirectoryUserDto | undefined> {
    if (this.map.has(id)) return this.map.get(id);
    await this.preFetch([id]);
    return this.map.get(id);
  }

  getUserDtoSync(id: string): UserDirectoryUserDto | undefined {
    return this.map.get(id);
  }

  // Compatibility: resolve used by existing preload code
  async resolve(
    ids: string[]
  ): Promise<Map<string, UserDirectoryUserDto | undefined>> {
    await this.preFetch(ids);
    const result = new Map<string, UserDirectoryUserDto | undefined>();
    for (const id of ids) result.set(id, this.map.get(id));
    return result;
  }

  get(id: string): UserDirectoryUserDto | undefined {
    return this.map.get(id);
  }

  prime(users: UserDirectoryUserDto[]): void {
    for (const u of users) {
      if (!u.loginUserId) continue;
      this.map.set(u.loginUserId, u);
      // A person who now arrives (a new user, an SSE update) is no longer absent.
      this.absent.delete(u.loginUserId);
      // Notify subscribers for this user
      const subs = this.subscribers.get(u.loginUserId);
      if (subs) {
        subs.forEach((cb) => {
          try {
            cb(u);
          } catch (e) {
            console.warn('[user-directory-cache] subscriber error', e);
          }
        });
      }
    }
  }

  setLocalProfileImage(userId: string, imageUrl: string): void {
    const existing = this.map.get(userId);
    if (!existing) return;

    // Through prime(), so subscribers are notified by the one path that already does it. A second
    // notify loop here would be the same rule written twice, free to drift.
    this.prime([{ ...existing, profileImageUrl: imageUrl }]);
  }

  subscribe(userId: string, callback: UserUpdatedCallback): () => void {
    let subs = this.subscribers.get(userId);
    if (!subs) {
      subs = new Set();
      this.subscribers.set(userId, subs);
    }
    subs.add(callback);
    return () => {
      subs.delete(callback);
      if (subs.size === 0) {
        this.subscribers.delete(userId);
      }
    };
  }

  async getAllUsersForMember(
    loginGroupId: string
  ): Promise<UserDirectoryUserDto[]> {
    const api = this.api();
    const resp = await api.getAllUsersForMember(loginGroupId);
    if (!resp.success || !resp.data.users) return [];
    const users = resp.data.users;
    // Prime the cache with all returned users
    this.prime(users);
    return users;
  }

  listen(): void {
    if (this.sseAttached) return;
    this.sseAttached = true;
    // Use the hub pattern: subscribeTopic handles both server subscription and event routing by topic
    const hub = getSseHub();
    hub.subscribeTopic('user-updated', (evt) => {
      const payload = (evt.data as unknown as SSEUserDirectoryUpdated).user;
      if (!payload?.loginUserId) return;
      this.applyUserUpdated(payload);
    });
  }

  applyUserUpdated(user: UserDirectoryUserDto): void {
    if (!user.loginUserId) return;
    this.prime([user]);
    this.editSubscribers.forEach((cb) => {
      try {
        cb(user);
      } catch (e) {
        console.warn('[user-directory-cache] edit subscriber error', e);
      }
    });
  }

  subscribeEdits(callback: UserUpdatedCallback): () => void {
    this.editSubscribers.add(callback);
    return () => {
      this.editSubscribers.delete(callback);
    };
  }
}

export function createUserDirectoryCache(): UserDirectoryCache {
  return new SimpleUserDirectoryCacheImpl();
}
